import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { QueuedSegment } from '~/types';

interface Audio2TextDB extends DBSchema {
  segments_queue: {
    key: string; // sessionId:segmentIndex
    value: QueuedSegment;
    indexes: {
      'by-state': string;
      'by-session': string;
    };
  };
}

const DB_NAME = 'audio2text';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<Audio2TextDB> | null = null;

/**
 * Generate key for segment
 */
function makeKey(sessionId: string, segmentIndex: number): string {
  return `${sessionId}:${segmentIndex}`;
}

/**
 * Get or create database connection
 */
async function getDB(): Promise<IDBPDatabase<Audio2TextDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<Audio2TextDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create segments queue store
      const segmentStore = db.createObjectStore('segments_queue', {
        keyPath: 'id',
      });

      segmentStore.createIndex('by-state', 'state');
      segmentStore.createIndex('by-session', 'sessionId');
    },
  });

  return dbInstance;
}

/**
 * Add segment to upload queue
 */
export async function enqueueSegment(segment: QueuedSegment): Promise<void> {
  const db = await getDB();
  const segmentWithId = {
    ...segment,
    id: makeKey(segment.sessionId, segment.segmentIndex),
  };
  await db.put('segments_queue', segmentWithId as any);
}

/**
 * Get segment from queue
 */
export async function getQueuedSegment(
  sessionId: string,
  segmentIndex: number
): Promise<QueuedSegment | undefined> {
  const db = await getDB();
  const key = makeKey(sessionId, segmentIndex);
  return db.get('segments_queue', key);
}

/**
 * Get all queued segments for upload
 */
export async function getQueuedSegmentsForUpload(limit = 10): Promise<QueuedSegment[]> {
  const db = await getDB();
  const now = Date.now();

  // Get all queued or failed segments
  const allSegments = await db.getAllFromIndex('segments_queue', 'by-state', 'queued');
  const failedSegments = await db.getAllFromIndex('segments_queue', 'by-state', 'failed');

  const segments = [...allSegments, ...failedSegments]
    .filter(seg => seg.nextRetryAt <= now)
    .slice(0, limit);

  return segments;
}

/**
 * Update segment state
 */
export async function updateSegmentState(
  sessionId: string,
  segmentIndex: number,
  updates: Partial<QueuedSegment>
): Promise<void> {
  const db = await getDB();
  const key = makeKey(sessionId, segmentIndex);
  const segment = await db.get('segments_queue', key);

  if (!segment) {
    throw new Error(`Segment not found: ${sessionId}:${segmentIndex}`);
  }

  const updated = { ...segment, ...updates };
  await db.put('segments_queue', updated as any);
}

/**
 * Delete segment from queue
 */
export async function deleteQueuedSegment(
  sessionId: string,
  segmentIndex: number
): Promise<void> {
  const db = await getDB();
  const key = makeKey(sessionId, segmentIndex);
  await db.delete('segments_queue', key);
}

/**
 * Get pending upload count for session
 */
export async function getPendingUploadCount(sessionId: string): Promise<number> {
  const db = await getDB();
  const segments = await db.getAllFromIndex('segments_queue', 'by-session', sessionId);

  return segments.filter(seg => seg.state !== 'uploaded').length;
}

/**
 * Clear all uploaded segments (cleanup)
 */
export async function clearUploadedSegments(olderThan = 5 * 60 * 1000): Promise<void> {
  const db = await getDB();
  const now = Date.now();
  const allSegments = await db.getAll('segments_queue');

  const toDelete = allSegments.filter(
    seg => seg.state === 'uploaded' && now - seg.nextRetryAt > olderThan
  );

  for (const seg of toDelete) {
    const key = makeKey(seg.sessionId, seg.segmentIndex);
    await db.delete('segments_queue', key);
  }
}
